import React, { useMemo,useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import { useSelector } from 'react-redux';

const chartStyle = {
  background: '#fff',
  borderRadius: '16px',
  padding: '20px',
  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
  flex: 1,
};

const chartContainer = {
  display: 'flex',
  gap: '20px',
  marginTop: '20px',
};

const dropdownStyle = {
  padding: '5px 10px',
  border: '1px solid #ccc',
  borderRadius: '6px',
  fontSize: '14px',
};

const ageCategories = ['0-1', '1-3', '3-6', '6-9', '9-15', '15+'];
const reproCategories = ['Spayed', 'Neutered', 'Intact'];

export default function AgeAndReproductiveCharts() {
  const { dashboardData } = useSelector((state) => state.dashboard);
  const [selectedSpeciesAge, setSelectedSpeciesAge] = useState('Dog');
  const [selectedSpeciesRepro, setSelectedSpeciesRepro] = useState('Dog');

  // Age Chart Series
  const ageSeries = useMemo(() => {
    if (!dashboardData?.age) return [];

    const speciesData = dashboardData.age[selectedSpeciesAge] || {};
    const maleData = ageCategories.map(age => speciesData.male?.[age] || 0);
    const femaleData = ageCategories.map(age => speciesData.female?.[age] || 0);

    return [
      { name: 'Male', data: maleData },
      { name: 'Female', data: femaleData }
    ];
  }, [dashboardData, selectedSpeciesAge]);

  const ageOptions = {
    chart: { type: 'bar', stacked: true, toolbar: { show: false } },
    plotOptions: {
      bar: { horizontal: false, columnWidth: '40%', borderRadius: 10 },
    },
    dataLabels: {
      enabled: true,
      style: { fontSize: '13px', fontWeight: 'bold', colors: ['#fff'] },
    },
    colors: ['#1E90FF', '#FF69B4'],
    xaxis: { categories: ageCategories },
    legend: { position: 'top', horizontalAlign: 'left' },
  };

  // Reproductive Chart Series
  const reproductiveSeries = useMemo(() => {
    if (!dashboardData?.reproductiveStatus) return [];

    const speciesKey = selectedSpeciesRepro.toLowerCase(); // 'dog', 'cat'
    const reproductiveList = dashboardData.reproductiveStatus[speciesKey] || [];
    const reproductiveData = reproductiveList[0] || {};

    return [
      {
        name: 'Spayed',
        data: reproCategories.map(cat => cat === 'Spayed' ? reproductiveData['Spayed'] || 0 : 0),
      },
      {
        name: 'Neutered',
        data: reproCategories.map(cat => cat === 'Neutered' ? reproductiveData['Neutered'] || 0 : 0),
      },
      {
        name: 'Intact',
        data: reproCategories.map(cat => cat === 'Intact' ? reproductiveData['Intact'] || 0 : 0),
      },
    ];
  }, [dashboardData, selectedSpeciesRepro]);

  const reproductiveOptions = {
    chart: { type: 'bar', toolbar: { show: false } },
    plotOptions: {
      bar: { columnWidth: '40%', borderRadius: 10 },
    },
    dataLabels: {
      enabled: true,
      style: { fontSize: '13px', fontWeight: 'bold', colors: ['#fff'] },
    },
    colors: ['#1E90FF', '#FF69B4', '#32CD32'],
    xaxis: { categories: reproCategories },
    legend: { position: 'top', horizontalAlign: 'left' },
  };

  if (!dashboardData) return null;

  return (
    <div style={chartContainer}>
      {/* Age Chart */}
      <div style={chartStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h4 style={{ margin: 0 }}>Age</h4>
          <select
            value={selectedSpeciesAge}
            onChange={(e) => setSelectedSpeciesAge(e.target.value)}
            style={dropdownStyle}
          >
            <option value="Dog">Dog</option>
            <option value="Cat">Cat</option>
          </select>
        </div>
        <ReactApexChart options={ageOptions} series={ageSeries} type="bar" height={300} />
      </div>

      {/* Reproductive Chart */}
      <div style={chartStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h4 style={{ margin: 0 }}>Reproductive Status</h4>
          <select
            value={selectedSpeciesRepro}
            onChange={(e) => setSelectedSpeciesRepro(e.target.value)}
            style={dropdownStyle}
          >
            <option value="Dog">Dog</option>
            <option value="Cat">Cat</option>
          </select>
        </div>
        <ReactApexChart options={reproductiveOptions} series={reproductiveSeries} type="bar" height={300} />
      </div>
    </div>
  );
}